import { ArrowDownTrayIcon } from '@heroicons/react/16/solid'
import { format, roundToNearestMinutes } from 'date-fns'
import { type FunctionComponent, useMemo } from 'react'
import { Icn } from 'tailwind-ui'
import { TimeFormats } from '@/common/datepicker/utilities/formats'
import { type InboxThreadEventEventableEmailType } from '@/state/entities/inbox/inbox.types'
import { useBoolean } from '@/hooks/use-color-mode-standalone'
import AttachmentIcon from '../context/attachment/icon'
import { ThreadPopover } from './common'
import { MessageRow, emailBodyClasses } from './message-row'

/** Strip an HTML email body down to readable text: drop <style>/<script>/<head>
 * (so their CSS doesn't leak as text), turn block tags + <br> into newlines,
 * remove remaining tags and decode the common entities. */
function htmlToText(html: string): string {
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6]|blockquote|table)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

// Lines that mark the start of quoted history / signatures / leaked CSS — we
// cut the body at the first one so the thread shows just the new reply.
const CUT_PATTERNS: RegExp[] = [
  /^---\s*PLEASE REPLY ABOVE THIS LINE\s*---/i,
  /\[thread#/i,
  /^-{2,}\s*$/, // standard "-- " signature delimiter
  /^_{5,}\s*$/,
  /^-----\s*Original Message\s*-----/i,
  /\bwrote:\s*$/i, // "On <date> … wrote:"
  /\bescribi[oó]:\s*$/i, // Spanish "… escribió:"
  /\ba écrit\s*:?\s*$/i, // French
  /\bschrieb:\s*$/i, // German
  /\bschreef:\s*$/i, // Dutch
  /\bha scritto:\s*$/i, // Italian
  /^Enviado desde\b/i, // "Sent from" (ES)
  /^Sent from my\b/i,
  /^Get Outlook for\b/i,
  /^From:\s/i, // quoted header block
  /^De:\s/i,
  /^>/, // quoted line
  /^\s*[#.][\w-]*\d{5,}/, // leaked CSS selector e.g. "#yiv123…"
  /\{[^}]*(margin|padding|text-align|width)\s*:/i, // leaked CSS rule
]

/** Keep only the customer's actual message: HTML→text, then cut at the first
 * quoted-history / signature / CSS marker. Falls back to the full text if no
 * marker is found. */
function extractReply(text: string): string {
  const lines = text.split('\n')
  let cut = lines.length
  for (let i = 0; i < lines.length; i++) {
    if (CUT_PATTERNS.some((re) => re.test(lines[i]))) {
      cut = i
      break
    }
  }
  return lines
    .slice(0, cut)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

const ThreadEmailBox: FunctionComponent<{
  event: InboxThreadEventEventableEmailType
  threadId: string
}> = ({ event }) => {
  const [showMore, setShowMore] = useBoolean()
  const from = useMemo(() => {
    return event.contacts.find((item: any) => item?.type === 'from')
  }, [event.contacts])

  // Extract just the meaningful message text from an incoming email: prefer the
  // plain-text body and drop everything from the signature delimiter ('--')
  // onward, so the collapsed view shows the customer's actual message rather
  // than their signature, social icons and quoted history. Falls back to
  // stripping tags from the trimmed HTML when no text body is available.
  const cleanBody = useMemo(() => {
    // Prefer the plain-text body; otherwise strip the HTML to text (dropping
    // leaked <style> CSS). Then cut quoted history / signatures.
    const raw = event.text_body
      ? event.text_body
      : htmlToText(event.html_body_trimmed || event.html_body || '')
    return extractReply(raw)
  }, [event.text_body, event.html_body_trimmed, event.html_body])

  const hasFullEmail = Boolean(event.html_body || event.html_body_trimmed)
  const senderName = from?.name || from?.email || event.subject || 'Customer'

  return (
    <MessageRow
      direction="incoming"
      name={senderName}
      email={from?.email || ''}
      meta={event.subject || from?.email}
      time={format(
        roundToNearestMinutes(event.created_at, { nearestTo: 10 }),
        TimeFormats.FourHourApple
      )}
      info={<ThreadPopover event={event} />}
    >
      {showMore && hasFullEmail ? (
        <div
          className={emailBodyClasses}
          dangerouslySetInnerHTML={{
            __html: event.html_body || event.html_body_trimmed,
          }}
        />
      ) : (
        <div className={`${emailBodyClasses} whitespace-pre-line`}>
          {cleanBody}
        </div>
      )}

      {event.attachments.length > 0 && (
        <div className="grid w-fit grid-cols-4 gap-4 pt-3">
          {event.attachments.map((item: any) => (
            <div key={item?.url} className="group/att relative overflow-hidden">
              <a
                href={item?.url.replace('http://', 'https://')}
                className="absolute -bottom-8 right-1 z-10 rounded-full p-2 transition-all duration-250 group-hover/att:bottom-1"
                download
              >
                <Icn icon={ArrowDownTrayIcon} />
              </a>
              <AttachmentIcon
                className="h-16 w-16 transition-all duration-250 group-hover/att:brightness-50"
                attachment={item}
              />
            </div>
          ))}
        </div>
      )}

      {hasFullEmail ? (
        <button
          type="button"
          onClick={setShowMore.toggle}
          className="mt-2 text-xs text-[var(--muted-foreground)] underline-offset-2 hover:text-[var(--foreground)] hover:underline"
        >
          {!showMore ? 'Show full email' : 'Show less'}
        </button>
      ) : null}
    </MessageRow>
  )
}

export default ThreadEmailBox
